/**
 * Sandbox API service.
 * Provides CRUD operations and lifecycle management for sandboxes.
 *
 * API Endpoints:
 * - GET /sandboxes - List all sandboxes
 * - GET /sandboxes/:id - Get sandbox by ID
 * - PUT /sandboxes - Create sandbox
 * - DELETE /sandboxes/:id - Delete sandbox
 * - POST /sandboxes/:id/start - Start sandbox
 * - POST /sandboxes/:id/stop - Stop sandbox
 *
 * @module services/sandboxService
 */

import { httpClient } from './httpClient';
import type {
  SandboxRequest,
  SandboxResponse,
  SnapshotRequest,
  CommitSandboxRequest,
  CommitSandboxResponse,
  AddPortRequest,
  PatchPortRequest,
  RemovePortRequest,
  UpdatePortsRequest,
  SandboxPort,
  EgressPolicy,
  EgressDecisionsResponse,
  SandboxStatsResponse,
  SandboxLifecyclePolicy,
  SandboxVolumeMount,
  AddPodVolumeMountsRequest,
} from '@/types';

const ENDPOINT = '/sandboxes';

/** Build the base URL for a specific sandbox, using group-scoped path when sandboxGroupId is provided. */
function sandboxBase(id: string, sandboxGroupId?: string): string {
  const encodedId = encodeURIComponent(id);
  if (sandboxGroupId) {
    return `/sandboxGroups/${encodeURIComponent(sandboxGroupId)}/sandboxes/${encodedId}`;
  }
  return `${ENDPOINT}/${encodedId}`;
}

/**
 * Sandbox Service
 * Encapsulates all sandbox API operations
 */
export const sandboxService = {
  /**
   * Get total count of sandboxes
   * @returns Promise<number> - Total count
   */
  async getCount(): Promise<number> {
    console.log('[SandboxService] Fetching sandboxes count...');
    try {
      const response = await httpClient.get<number>(`${ENDPOINT}/count`);
      console.log('[SandboxService] Total sandboxes:', response.data);
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to fetch sandboxes count:', error);
      throw error;
    }
  },

  /**
   * List sandboxes with pagination
   * @param page - Page number (1-based, default: 1)
   * @param pageSize - Number of items per page (default: 25, max: 100)
   * @returns Promise<SandboxResponse[]> - Array of sandboxes
   */
  async list(page: number = 1, pageSize: number = 25): Promise<SandboxResponse[]> {
    console.log('[SandboxService] Fetching sandboxes - Page:', page, 'PageSize:', pageSize);
    try {
      const response = await httpClient.get<SandboxResponse[]>(
        `${ENDPOINT}?page=${page}&pageSize=${pageSize}`
      );
      console.log('[SandboxService] Fetched', response.data.length, 'sandboxes');
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to fetch sandboxes:', error);
      throw error;
    }
  },

  /**
   * Get a sandbox by ID
   * @param id - The sandbox ID
   * @returns Promise<SandboxResponse> - The sandbox
   */
  async getById(id: string, sandboxGroupId?: string): Promise<SandboxResponse> {
    console.log('[SandboxService] Fetching sandbox:', id);
    try {
      const response = await httpClient.get<SandboxResponse>(sandboxBase(id, sandboxGroupId));
      console.log('[SandboxService] Fetched sandbox:', response.data.id);
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to fetch sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Create a new sandbox
   * @param request - The sandbox request payload
   * @returns Promise<SandboxResponse> - The created sandbox
   */
  async create(request: SandboxRequest): Promise<SandboxResponse> {
    const diskImageId = request.sourcesRef?.diskImage?.id;
    console.log('[SandboxService] Creating sandbox with disk image:', diskImageId);
    console.log('[SandboxService] Request payload:', JSON.stringify(request, null, 2));
    try {
      // Pass includeDebug=true to get performance traces for waterfall visualization
      const response = await httpClient.put<SandboxResponse>(
        `${ENDPOINT}?includeDebug=true`,
        request
      );
      console.log('[SandboxService] Created sandbox:', response.data.id);
      return response.data;
    } catch (error: unknown) {
      console.error('[SandboxService] Failed to create sandbox:', error);
      const axiosError = error as {
        message?: string;
        response?: { data?: unknown; status?: number };
      };
      console.error('[SandboxService] Error details:', {
        message: axiosError.message,
        response: axiosError.response?.data,
        status: axiosError.response?.status,
      });
      throw error;
    }
  },

  /**
   * Delete a sandbox by ID
   * @param id - The sandbox ID to delete
   * @returns Promise<void>
   */
  async delete(id: string, sandboxGroupId?: string): Promise<void> {
    console.log('[SandboxService] Deleting sandbox:', id);
    try {
      await httpClient.delete(sandboxBase(id, sandboxGroupId));
      console.log('[SandboxService] Deleted sandbox:', id);
    } catch (error) {
      console.error('[SandboxService] Failed to delete sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Resume a stopped sandbox
   * @param id - The sandbox ID to resume
   * @returns Promise<void>
   */
  async resume(id: string, sandboxGroupId?: string): Promise<void> {
    console.log('[SandboxService] Resuming sandbox:', id);
    try {
      await httpClient.post(`${sandboxBase(id, sandboxGroupId)}/resume`);
      console.log('[SandboxService] Resumed sandbox:', id);
    } catch (error) {
      console.error('[SandboxService] Failed to resume sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Stop a running sandbox
   * @param id - The sandbox ID to stop
   * @returns Promise<void>
   */
  async stop(id: string, sandboxGroupId?: string): Promise<void> {
    console.log('[SandboxService] Stopping sandbox:', id);
    try {
      await httpClient.post(`${sandboxBase(id, sandboxGroupId)}/stop`);
      console.log('[SandboxService] Stopped sandbox:', id);
    } catch (error) {
      console.error('[SandboxService] Failed to stop sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Create a snapshot of a sandbox
   * @param id - The sandbox ID to snapshot
   * @param request - Optional snapshot request payload (labels)
   * @returns Promise<{ snapshotId: string }> - The created snapshot ID
   */
  async createSnapshot(
    id: string,
    request?: SnapshotRequest,
    sandboxGroupId?: string
  ): Promise<{ snapshotId: string }> {
    console.log('[SandboxService] Creating snapshot for sandbox:', id);

    // Backend expects a JSON body with an optional labels dictionary.
    // Default to an empty dictionary for now.
    const body: SnapshotRequest = request ?? { labels: {} };

    try {
      const response = await httpClient.post<{ id: string }>(
        `${sandboxBase(id, sandboxGroupId)}/snapshot`,
        body
      );
      console.log('[SandboxService] Created snapshot:', response.data.id);
      return { snapshotId: response.data.id };
    } catch (error) {
      console.error('[SandboxService] Failed to create snapshot for sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Execute a command in a sandbox
   * @param id - The sandbox ID
   * @param command - The command to execute
   * @param workingDirectory - Optional working directory
   * @returns Promise<ExecuteCommandResponse> - The command execution result
   */
  async executeCommand(
    id: string,
    command: string,
    workingDirectory?: string,
    containerName?: string,
    sandboxGroupId?: string
  ): Promise<import('@/types').ExecuteCommandResponse> {
    console.log('[SandboxService] Executing command in sandbox:', id, 'Command:', command);
    const containerParam = containerName
      ? `?containerName=${encodeURIComponent(containerName)}`
      : '';
    try {
      const response = await httpClient.post<import('@/types').ExecuteCommandResponse>(
        `${sandboxBase(id, sandboxGroupId)}/executeShellCommand${containerParam}`,
        { command, workingDirectory }
      );
      console.log('[SandboxService] Command executed. Exit code:', response.data.exitCode);
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to execute command in sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Create a batch of sandboxes from the same disk image or snapshot
   * @param request - The batch sandbox request payload
   * @returns Promise<BatchSandboxResponse> - The batch creation result
   */
  async createBatch(
    request: import('@/types').BatchSandboxRequest
  ): Promise<import('@/types').BatchSandboxResponse> {
    console.log('[SandboxService] Creating batch of', request.count, 'sandboxes');
    console.log('[SandboxService] Request payload:', JSON.stringify(request, null, 2));
    try {
      const response = await httpClient.put<import('@/types').BatchSandboxResponse>(
        `${ENDPOINT}/batch`,
        request
      );
      console.log(
        '[SandboxService] Batch creation completed:',
        response.data.succeeded,
        'succeeded,',
        response.data.failed,
        'failed'
      );
      return response.data;
    } catch (error: unknown) {
      console.error('[SandboxService] Failed to create batch:', error);
      const axiosError = error as {
        message?: string;
        response?: { data?: unknown; status?: number };
      };
      console.error('[SandboxService] Error details:', {
        message: axiosError.message,
        response: axiosError.response?.data,
        status: axiosError.response?.status,
      });
      throw error;
    }
  },

  /**
   * Add a port to a sandbox
   * @param id - The sandbox ID
   * @param request - The port to add (name is optional, port is required)
   * @returns Promise<SandboxPort> - The added port with URL
   */
  async addPort(
    id: string,
    request: AddPortRequest,
    sandboxGroupId?: string
  ): Promise<SandboxPort> {
    console.log('[SandboxService] Adding port to sandbox:', id, 'Port:', request.port);
    try {
      const response = await httpClient.post<{ ports: SandboxPort[] }>(
        `${sandboxBase(id, sandboxGroupId)}/ports/add`,
        request
      );
      console.log('[SandboxService] Added port:', response.data);
      // API returns { ports: SandboxPort[] } — find the port we just added
      const addedPort =
        response.data.ports?.find((p) => p.port === request.port) ?? response.data.ports?.[0];
      if (!addedPort) {
        throw new Error(`Port ${request.port} was not found in the API response`);
      }
      return addedPort;
    } catch (error) {
      console.error('[SandboxService] Failed to add port to sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Remove a port from a sandbox
   * @param id - The sandbox ID
   * @param request - The port to remove (by name or port number)
   * @returns Promise<void>
   */
  async removePort(id: string, request: RemovePortRequest, sandboxGroupId?: string): Promise<void> {
    console.log('[SandboxService] Removing port from sandbox:', id, 'Request:', request);
    try {
      await httpClient.post(`${sandboxBase(id, sandboxGroupId)}/ports/remove`, request);
      console.log('[SandboxService] Removed port from sandbox:', id);
    } catch (error) {
      console.error('[SandboxService] Failed to remove port from sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Patch (partially update) a single port on a sandbox
   * @param id - The sandbox ID
   * @param request - The port fields to update
   * @returns Promise<SandboxPort[]> - All ports after the update
   */
  async patchPort(
    id: string,
    request: PatchPortRequest,
    sandboxGroupId?: string
  ): Promise<SandboxPort[]> {
    console.log('[SandboxService] Patching port on sandbox:', id, 'Request:', request);
    try {
      const response = await httpClient.patch<{ ports: SandboxPort[] }>(
        `${sandboxBase(id, sandboxGroupId)}/ports`,
        request
      );
      console.log('[SandboxService] Patched port:', response.data);
      return response.data.ports ?? [];
    } catch (error) {
      console.error('[SandboxService] Failed to patch port on sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Update all ports on a sandbox
   * @param id - The sandbox ID
   * @param request - The ports to set
   * @returns Promise<SandboxPort[]> - The updated ports
   */
  async updatePorts(
    id: string,
    request: UpdatePortsRequest,
    sandboxGroupId?: string
  ): Promise<SandboxPort[]> {
    console.log('[SandboxService] Updating ports on sandbox:', id, 'Ports:', request.ports);
    try {
      const response = await httpClient.put<SandboxPort[]>(
        `${sandboxBase(id, sandboxGroupId)}/ports`,
        request
      );
      console.log('[SandboxService] Updated ports:', response.data);
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to update ports on sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Set the egress policy for a running sandbox.
   * POST /sandboxes/:id/egresspolicy
   */
  async setEgressPolicy(
    id: string,
    policy: EgressPolicy,
    sandboxGroupId?: string
  ): Promise<EgressPolicy> {
    console.log('[SandboxService] Setting egress policy for sandbox:', id, 'Policy:', policy);
    try {
      const response = await httpClient.post<EgressPolicy>(
        `${sandboxBase(id, sandboxGroupId)}/egresspolicy`,
        policy
      );
      console.log('[SandboxService] Updated egress policy:', response.data);
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to set egress policy for sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Set the lifecycle policy for a sandbox.
   * POST /sandboxes/:id/lifecycle
   */
  async setLifecyclePolicy(
    id: string,
    policy: SandboxLifecyclePolicy,
    sandboxGroupId?: string
  ): Promise<SandboxResponse> {
    console.log('[SandboxService] Setting lifecycle policy for sandbox:', id, 'Policy:', policy);
    try {
      const response = await httpClient.post<SandboxResponse>(
        `${sandboxBase(id, sandboxGroupId)}/lifecycle`,
        policy
      );
      console.log('[SandboxService] Updated lifecycle policy:', response.data);
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to set lifecycle policy for sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Get egress decisions (allowed/denied requests) for a sandbox.
   * GET /sandboxes/:id/egress-decisions
   */
  async getEgressDecisions(id: string, sandboxGroupId?: string): Promise<EgressDecisionsResponse> {
    console.log('[SandboxService] Fetching egress decisions for sandbox:', id);
    try {
      const response = await httpClient.get<EgressDecisionsResponse>(
        `${sandboxBase(id, sandboxGroupId)}/egress-decisions`
      );
      console.log('[SandboxService] Fetched egress decisions:', response.data);
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to fetch egress decisions for sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Commit a running sandbox to a new two-layer disk image.
   * This creates a new disk image with the changes made in the current sandbox instance.
   * POST /sandboxes/:id/commit
   * @param id - The sandbox ID
   * @param request - Optional commit request payload (labels)
   * @returns Promise<CommitSandboxResponse> - The created disk image
   */
  async commitSandbox(
    id: string,
    request?: CommitSandboxRequest,
    sandboxGroupId?: string
  ): Promise<CommitSandboxResponse> {
    console.log('[SandboxService] Committing sandbox to disk image:', id);

    // Backend expects a JSON body with an optional labels dictionary.
    const body: CommitSandboxRequest = request ?? { labels: {} };

    try {
      const response = await httpClient.post<CommitSandboxResponse>(
        `${sandboxBase(id, sandboxGroupId)}/commit`,
        body
      );
      console.log(
        '[SandboxService] Committed sandbox, created disk image:',
        response.data.diskImage.id
      );
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to commit sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Add a connection to a running sandbox
   * POST /sandboxes/:id/connections/add
   * @param id - The sandbox ID
   * @param connectionId - The connection ID to add
   * @returns Promise<void>
   */
  async addConnection(id: string, connectionId: string, sandboxGroupId?: string): Promise<void> {
    console.log('[SandboxService] Adding connection to sandbox:', id, 'Connection:', connectionId);
    try {
      await httpClient.post<void>(`${sandboxBase(id, sandboxGroupId)}/connections/add`, {
        connectionId,
      });
      console.log('[SandboxService] Added connection to sandbox');
    } catch (error) {
      console.error('[SandboxService] Failed to add connection to sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Get statistics for a sandbox, including token usage if LLM interception is enabled.
   * GET /sandboxes/:id/stats
   * @param id - The sandbox ID
   * @returns Promise<SandboxStatsResponse> - The sandbox statistics
   */
  async getStats(id: string, sandboxGroupId?: string): Promise<SandboxStatsResponse> {
    console.log('[SandboxService] Fetching stats for sandbox:', id);
    try {
      const response = await httpClient.get<SandboxStatsResponse>(
        `${sandboxBase(id, sandboxGroupId)}/stats`
      );
      console.log('[SandboxService] Fetched stats:', response.data);
      return response.data;
    } catch (error) {
      console.error('[SandboxService] Failed to fetch stats for sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Add a volume mount to a running sandbox
   * POST /sandboxes/:id/volumes/add
   * @param id - The sandbox ID
   * @param volumeMount - The volume mount configuration
   * @returns Promise<void>
   */
  async addVolumeMount(
    id: string,
    volumeMount: SandboxVolumeMount,
    containerName?: string,
    sandboxGroupId?: string
  ): Promise<void> {
    console.log(
      '[SandboxService] Adding volume to sandbox:',
      id,
      'Volume:',
      volumeMount.volumeName
    );
    const containerParam = containerName
      ? `?containerName=${encodeURIComponent(containerName)}`
      : '';
    try {
      await httpClient.post<void>(
        `${sandboxBase(id, sandboxGroupId)}/volumes/add${containerParam}`,
        {
          volumeMount,
        }
      );
      console.log('[SandboxService] Added volume to sandbox');
    } catch (error) {
      console.error('[SandboxService] Failed to add volume to sandbox:', id, error);
      throw error;
    }
  },

  /**
   * Add pod-level volumes and/or mount volumes into pod containers.
   * POST /sandboxes/:id/pod-volumes/add
   * @param id - The sandbox ID
   * @param request - Batch request with optional new volumes and per-container mount specs
   * @returns Promise<void>
   */
  async addPodVolumeMounts(
    id: string,
    request: AddPodVolumeMountsRequest,
    sandboxGroupId?: string
  ): Promise<void> {
    console.log(
      '[SandboxService] Adding pod volume mounts to sandbox:',
      id,
      'Volumes:',
      request.volumes?.length ?? 0,
      'Container mounts:',
      request.containerMounts.length
    );
    try {
      await httpClient.post<void>(`${sandboxBase(id, sandboxGroupId)}/pod-volumes/add`, request);
      console.log('[SandboxService] Added pod volume mounts to sandbox');
    } catch (error) {
      console.error('[SandboxService] Failed to add pod volume mounts to sandbox:', id, error);
      throw error;
    }
  },
};
